import { useRef, useState } from 'react';
import type { Daypart } from '@coffman-tm/shared';
import { Dropdown } from '../design';

/**
 * Per-line day/swing selector for the PDF Inbox labor table. Visually mirrors
 * InlineClassSelect, but is a dumb controlled select: it does NOT persist on
 * its own — the chosen daypart rides the block-save payload with hours/rate.
 * The parent's onChange re-prices the row (day vs swing rate).
 */
const DAYPART_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'swing', label: 'Swing' },
];

export interface InlineDaypartSelectProps {
  value: Daypart;
  onChange: (next: Daypart) => void;
  disabled?: boolean;
  ariaLabel?: string;
  width?: number;
}

export function InlineDaypartSelect({
  value,
  onChange,
  disabled,
  ariaLabel,
  width = 92,
}: InlineDaypartSelectProps) {
  // Local draft so the cell can show the in-flight selection while the
  // save round-trips. The parent rebinds `value` post-refetch.
  const [draft, setDraft] = useState<Daypart>(value);
  // The control is a button now, not a <select>, so "is the user mid-pick?"
  // is asked of THIS cell's own trigger rather than of the tag name of
  // whatever happens to be focused. Same moment, narrower question.
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Re-sync when upstream value changes (parent refetch, etc).
  if (draft !== value && document.activeElement !== triggerRef.current) {
    // Only resync when the user isn't actively editing. Otherwise their
    // in-progress pick would snap back to server state mid-interaction.
    setDraft(value);
  }

  return (
    <Dropdown
      ref={triggerRef}
      uiSize="sm"
      options={DAYPART_OPTIONS}
      value={draft}
      onChange={(v) => {
        const next = v as Daypart;
        setDraft(next);
        if (next !== value) onChange(next);
      }}
      disabled={disabled}
      aria-label={ariaLabel ?? 'Daypart'}
      style={{ width }}
    />
  );
}
